// FileUploader.tsx
import { createSignal } from "solid-js";
import { validateFile } from "./fileValidations";
import { BatchFileInput, BatchSignedUrl } from "./types";

interface FileUploaderProps {
	prefix: string;
	getSignedUrls: ( files: BatchFileInput[] ) => Promise<BatchSignedUrl[]>;
}

interface UploaderEntry {
	file: File;
	fullPath: string;
	progress: number;
	status: string;
}

interface BlockedEntry {
	name: string;
	fullPath: string;
	reason: string;
}

const FileUploader = ( props: FileUploaderProps ) => {
	const [files, setFiles] = createSignal<UploaderEntry[]>( [] );
	const [blocked, setBlocked] = createSignal<BlockedEntry[]>( [] );
	const [uploading, setUploading] = createSignal( false );

	// ---------------------------
	// FOLDER RECURSION
	// ---------------------------
	const walkFileTree = async (
		entry: any,
		pathPrefix = ""
	): Promise<File[]> => {
		return new Promise( ( resolve ) => {
			if ( entry.isFile ) {
				entry.file( ( file: File ) => {
					const fullPath = pathPrefix + file.name;
					const check = validateFile( file, fullPath );

					if ( check.blocked ) {
						setBlocked( ( prev ) => [
							...prev,
							{ name: file.name, fullPath, reason: check.reason }
						] );
						resolve( [] );
						return;
					}

					( file as any ).fullPath = fullPath;
					resolve( [file] );
				} );
			} else if ( entry.isDirectory ) {
				const reader = entry.createReader();
				reader.readEntries( async ( entries: any[] ) => {
					const results: File[] = [];
					for ( const e of entries ) {
						const res = await walkFileTree(
							e,
							pathPrefix + entry.name + "/"
						);
						results.push( ...res );
					}
					resolve( results );
				} );
			}
		} );
	};

	// ---------------------------
	// PROCESS DRAG-DROP ITEMS
	// ---------------------------
	const processItems = async ( items: any[] ) => {
		let collected: File[] = [];

		for ( const item of items ) {
			const entry = item.webkitGetAsEntry?.();

			if ( entry ) {
				const res = await walkFileTree( entry );
				collected.push( ...res );
			} else {
				const file = item.getAsFile?.();
				if ( file ) {
					const fullPath = file.name;
					const check = validateFile( file, fullPath );

					if ( check.blocked ) {
						setBlocked( ( prev ) => [
							...prev,
							{ name: file.name, fullPath, reason: check.reason }
						] );
						continue;
					}

					( file as any ).fullPath = fullPath;
					collected.push( file );
				}
			}
		}

		setFiles( ( prev ) => [
			...prev,
			...collected.map( ( file ) => ( {
				file,
				fullPath: ( file as any ).fullPath,
				progress: 0,
				status: "pending"
			} ) )
		] );
	};

	// ---------------------------
	// PROCESS FILE INPUT (manual)
	// ---------------------------
	const processInput = ( fileList: FileList ) => {
		const accepted: UploaderEntry[] = [];

		for ( const file of Array.from( fileList ) ) {
			const fullPath = ( file as any ).webkitRelativePath || file.name;
			const check = validateFile( file, fullPath );

			if ( check.blocked ) {
				setBlocked( ( prev ) => [
					...prev,
					{ name: file.name, fullPath, reason: check.reason }
				] );
				continue;
			}

			accepted.push( {
				file,
				fullPath,
				progress: 0,
				status: "pending"
			} );
		}

		setFiles( ( prev ) => [...prev, ...accepted] );
	};

	// ---------------------------
	// DRAG EVENTS
	// ---------------------------
	const onDrop = async ( e: DragEvent ) => {
		e.preventDefault();
		const items = [...e.dataTransfer!.items];
		await processItems( items );
	};

	// ---------------------------
	// UPLOAD ALL FILES
	// ---------------------------
	const uploadAll = async () => {
		setUploading( true );

		// Build payload for batch signed URLs
		const payload: BatchFileInput[] = files().map( ( f ) => ( {
			key: `${props.prefix}/${f.fullPath}`,
			contentType: f.file.type || "application/octet-stream"
		} ) );

		// Get signed URLs
		const signed = await props.getSignedUrls( payload );

		// Map by key
		const urlMap: Record<string, string> = {};
		signed.forEach( ( s ) => ( urlMap[s.key] = s.url ) );

		// Upload files
		for ( const entry of files() ) {
			const key = `${props.prefix}/${entry.fullPath}`;
			const url = urlMap[key];

			await new Promise<void>( ( resolve, reject ) => {
				const xhr = new XMLHttpRequest();
				xhr.open( "PUT", url );

				xhr.upload.onprogress = ( e ) => {
					if ( e.lengthComputable ) {
						entry.progress = Math.ceil(
							( e.loaded / e.total ) * 100
						);
						setFiles( [...files()] );
					}
				};

				xhr.onload = () => {
					entry.progress = 100;
					entry.status = "done";
					setFiles( [...files()] );
					resolve();
				};

				xhr.onerror = () => {
					entry.status = "error";
					setFiles( [...files()] );
					reject();
				};

				xhr.send( entry.file );
			} );
		}

		setUploading( false );
	};

	// ---------------------------
	// UI
	// ---------------------------
	return (
		<div class="space-y-4">

			<div
				class="border-2 border-dashed p-10 text-center rounded-xl"
				onDragOver={( e ) => e.preventDefault()}
				onDrop={onDrop}
			>
				Drag & drop files or folders here
			</div>

			<div class="flex gap-2">
				<label>
					<span class="px-4 py-2 bg-gray-200 rounded cursor-pointer">
						Add files
					</span>
					<input
						type="file"
						multiple
						class="hidden"
						onChange={( e ) => e.target.files && processInput( e.target.files )}
					/>
				</label>

				<label>
					<span class="px-4 py-2 bg-gray-200 rounded cursor-pointer">
						Add folder
					</span>
					<input
						type="file"
						webkitdirectory="true"
						class="hidden"
						onChange={( e ) =>
							e.target.files && processInput( e.target.files )
						}
					/>
				</label>
			</div>

			{/* Accepted Files */}
			<table class="w-full text-sm border">
				<thead>
					<tr class="bg-gray-100">
						<th class="p-2 text-left">Name</th>
						<th class="p-2 text-left">Path</th>
						<th class="p-2 text-left">Size</th>
						<th class="p-2">Progress</th>
					</tr>
				</thead>
				<tbody>
					{files().map( ( f ) => (
						<tr class="border-t">
							<td class="p-2">{f.file.name}</td>
							<td class="p-2">{f.fullPath}</td>
							<td class="p-2">{( f.file.size / 1024 ).toFixed( 1 )} KB</td>
							<td class="p-2 w-40">
								<div class="bg-gray-200 h-2 rounded">
									<div
										class="bg-blue-500 h-2 rounded"
										style={{ width: `${f.progress}%` }}
									/>
								</div>
							</td>
						</tr>
					) )}
				</tbody>
			</table>

			{/* Blocked Files */}
			{blocked().length > 0 && (
				<div class="mt-4">
					<h3 class="font-bold mb-2">Blocked Files</h3>
					<table class="w-full text-sm border">
						<thead>
							<tr class="bg-gray-100">
								<th class="p-2 text-left">Name</th>
								<th class="p-2 text-left">Path</th>
								<th class="p-2 text-left">Reason</th>
							</tr>
						</thead>
						<tbody>
							{blocked().map( ( b ) => (
								<tr class="border-t">
									<td class="p-2">{b.name}</td>
									<td class="p-2">{b.fullPath}</td>
									<td class="p-2">{b.reason}</td>
								</tr>
							) )}
						</tbody>
					</table>
				</div>
			)}

			{/* Upload Button */}
			{files().length > 0 && (
				<button
					type="button"
					class="px-4 py-2 bg-blue-600 text-white rounded"
					disabled={uploading()}
					onClick={uploadAll}
				>
					{uploading() ? "Uploading…" : "Upload All"}
				</button>
			)}
		</div>
	);
};

export default FileUploader;
